import { ArrowRight, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { copy } from '../../copy/investor-copy'

const FILTER_DELAY_MS = 300

/**
 * The name search: a pill with the orange border of the prototype's search box. Typing filters the
 * list after a short pause.
 */
export function SearchBox({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  const [text, setText] = useState(value)
  useEffect(() => {
    // The list echoes back what was filtered; a draft typed since then is not overwritten by that echo.
    setText((draft) => (draft.trim() === value ? draft : value))
  }, [value])
  // The parent passes a new callback on every render; the timer keeps the latest one without restarting.
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  })
  useEffect(() => {
    if (text.trim() === value) return
    const timer = setTimeout(() => onChangeRef.current(text.trim()), FILTER_DELAY_MS)
    return () => clearTimeout(timer)
  }, [text, value])

  return (
    <search>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          onChange(text.trim())
        }}
        className="flex items-center gap-2 rounded-full border border-primary bg-[#0F0F10] py-2 pl-4 pr-2 shadow-[0_0_0_3px_rgb(249_115_22/0.12)]"
      >
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={100}
          placeholder={copy('search.placeholder')}
          aria-label={copy('search.aria.box')}
          className="min-w-0 flex-1 bg-transparent text-[15px] text-foreground outline-none placeholder:text-muted-foreground"
        />
        {text && (
          <button
            type="button"
            aria-label={copy('search.aria.clear')}
            onClick={() => {
              setText('')
              onChange('')
            }}
            className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        )}
        <button
          type="submit"
          aria-label={copy('search.aria.submit')}
          className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary-hover"
        >
          <ArrowRight className="size-5" />
        </button>
      </form>
    </search>
  )
}
